import { useCallback, useMemo, useState } from 'react';
import { getCandidates, getDeals, postMark } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { Candidate, CandidateList } from '../api/model';
import type { MarkRequest } from '../api/types';
import { AllocationEditor, newAllocRow, validateAllocations, type AllocRow } from '../components/AllocationEditor';
import { ErrorNotice, Loading, Notice } from '../components/Async';
import { Dialog } from '../components/Dialog';
import { HelpLabel } from '../components/Help';
import { Table, type Column } from '../components/Table';
import { Dash, Val } from '../components/Val';
import { issueText, reasonText } from '../i18n/reasons.hu';
import { formatDecimal } from '../lib/decimal';
import { TYPE_LABEL } from '../lib/labels';
import { Link } from '../lib/router';
import { formatUtc, parseUtcInput, presetWindow, toApiUtc, utcInputValue, type WindowPreset } from '../lib/time';

type Preset = WindowPreset | 'custom';
const PRESETS: { value: Preset; label: string }[] = [
  { value: 'hour', label: '1 óra' },
  { value: 'today', label: 'Ma' },
  { value: 'week', label: '7 nap' },
  { value: 'quarter', label: '90 nap' },
  { value: 'custom', label: 'Egyéni…' },
];

function statusOf(c: Candidate, checked: boolean): { text: string; cls: string } {
  if (c.already_logged) {
    if (c.already_logged_voided) return { text: 'Naplózva, de visszavonva', cls: 'pill' };
    return { text: c.logged_deals.length ? `Már naplóban · ${c.logged_deals.map((a) => `#${a.deal_id}`).join(', ')}` : 'Már naplóban', cls: 'pill done' };
  }
  if (checked) return { text: 'Kézi · rögzítésre', cls: 'pill warn' };
  if (c.is_bot === true) return { text: 'Margin bot (gbx)', cls: 'pill bot' };
  return { text: 'Nincs megjelölve', cls: 'pill' };
}

export function MarkPage() {
  const [preset, setPreset] = useState<Preset>('week');
  const [customFrom, setCustomFrom] = useState(() => utcInputValue(new Date(Date.now() - 7 * 86_400_000)));
  const [customTo, setCustomTo] = useState(() => utcInputValue(new Date()));
  const [account, setAccount] = useState<'cross' | 'isolated'>('cross');
  const [isolatedSymbol, setIsolatedSymbol] = useState('BTCUSDC');
  const [symbols, setSymbols] = useState('BTCUSDC,BTCUSDT');
  const [assets, setAssets] = useState('BTC,USDC,USDT');

  const [list, setList] = useState<CandidateList | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<Error | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dealMode, setDealMode] = useState<'new' | 'existing'>('new');
  const [existingDeal, setExistingDeal] = useState<number | null>(null);
  const [dealName, setDealName] = useState('');
  const [dealRef, setDealRef] = useState('BTC');
  const [dealTags, setDealTags] = useState('');
  const [note, setNote] = useState('');
  const [allocs, setAllocs] = useState<Record<string, AllocRow[]>>({});
  const [splitting, setSplitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [saved, setSaved] = useState<{ created: number; dealId: number; dealName: string; skipped: { external_id: string; reason: string }[]; merges: number } | null>(null);

  const dealsState = useApi(getDeals);
  const openDeals = useMemo(() => (dealsState.data ?? []).filter((d) => d.status === 'open'), [dealsState.data]);

  const windowRange = useMemo(() => {
    if (preset === 'custom') {
      const from = parseUtcInput(customFrom);
      const to = parseUtcInput(customTo);
      return from && to && from < to ? { from, to } : null;
    }
    return presetWindow(preset, new Date());
  }, [preset, customFrom, customTo]);

  const accountParam = account === 'cross' ? 'cross' : `isolated:${isolatedSymbol.trim().toUpperCase()}`;
  const canFetch = !!windowRange && !fetching && (account === 'cross' || isolatedSymbol.trim().length > 0);

  const fetchList = useCallback(async () => {
    const range = preset === 'custom' ? windowRange : presetWindow(preset, new Date());
    if (!range) return;
    setFetching(true);
    setFetchError(null);
    setSaved(null);
    try {
      const res = await getCandidates({ from: toApiUtc(range.from), to: toApiUtc(range.to), account: accountParam, symbols: symbols.replace(/\s/g, ''), assets: assets.replace(/\s/g, '') });
      setList(res);
      setSelected(new Set());
      setAllocs({});
    } catch (e) {
      setList(null);
      setFetchError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setFetching(false);
    }
  }, [preset, windowRange, accountParam, symbols, assets]);

  const items = list?.items ?? [];
  const selectedItems = items.filter((c) => selected.has(c.external_id) && !c.already_logged);
  const selCount = selectedItems.length;

  const toggle = (c: Candidate) => {
    if (c.already_logged) return;
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(c.external_id)) n.delete(c.external_id);
      else n.add(c.external_id);
      return n;
    });
  };

  const allocationsValid = selectedItems.every((c) => {
    const rows = allocs[c.external_id];
    return !rows || rows.length === 0 || validateAllocations(rows, c.quantity).ok;
  });
  const dealValid = dealMode === 'new' ? dealName.trim().length > 0 : existingDeal !== null;
  const canSave = selCount > 0 && dealValid && allocationsValid && !saving;

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    const allocations = selectedItems.flatMap((c) =>
      (allocs[c.external_id] ?? []).filter((r) => r.dealId !== null).map((r) => ({ external_id: c.external_id, deal_id: r.dealId as number, quantity: r.qty })),
    );
    const body: MarkRequest = {
      candidates: selectedItems.map((c) => c.external_id),
      deal: dealMode === 'existing' && existingDeal !== null ? { id: existingDeal } : { new: { name: dealName.trim(), reference_asset: dealRef || null, tags: dealTags.split(',').map((t) => t.trim()).filter(Boolean), note: note.trim() } },
      ...(allocations.length ? { allocations } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    try {
      const res = await postMark(body);
      dealsState.reload();
      await fetchList();
      setSaved({ created: res.created, dealId: res.deal.id, dealName: res.deal.name, skipped: res.skipped, merges: res.merge_suggestions });
    } catch (e) {
      setSaveError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setSaving(false);
    }
  };

  const columns: Column<Candidate>[] = [
    {
      key: 'sel', label: 'Kijelölés', srOnly: true, width: '36px',
      render: (c) => (
        <input type="checkbox" checked={!!c.already_logged || selected.has(c.external_id)} disabled={!!c.already_logged} title={c.logged_deals.length ? `Naplózva: ${c.logged_deals.map((a) => `#${a.deal_id} ${a.deal_name ?? ''}`.trim()).join(', ')}` : undefined} onChange={() => toggle(c)} aria-label={`Kézinek jelölés: ${TYPE_LABEL[c.type]} ${formatUtc(c.occurred_at)}`} />
      ),
    },
    { key: 'time', label: 'Időpont (UTC)', sortValue: (c) => c.occurred_at, render: (c) => <span className="mono">{formatUtc(c.occurred_at)}</span> },
    { key: 'type', label: 'Típus', sortValue: (c) => TYPE_LABEL[c.type], render: (c) => TYPE_LABEL[c.type] },
    { key: 'symbol', label: 'Pár / coin', sortValue: (c) => c.symbol, render: (c) => c.symbol },
    { key: 'qty', label: 'Mennyiség', numeric: true, sortValue: (c) => c.quantity, render: (c) => <span className="mono">{formatDecimal(c.quantity)}</span> },
    { key: 'price', label: 'Ár', numeric: true, sortValue: (c) => c.price, render: (c) => <span className="mono"><Val value={c.price} reason="not_applicable" maxDp={4} /></span> },
    { key: 'fee', label: 'Díj', numeric: true, sortValue: (c) => c.fee, render: (c) => <span className="mono">{c.fee === null ? <Dash reason="not_applicable" /> : `${formatDecimal(c.fee)} ${c.fee_asset ?? ''}`}</span> },
    { key: 'cid', label: 'Kliens-azonosító', help: 'clientId', ariaLabel: 'kliens-azonosító', sortValue: (c) => c.client_order_id, render: (c) => <span className="mono small">{c.client_order_id ?? '— (kölcsön / transzfer)'}</span> },
    { key: 'issues', label: 'Megjegyzés', srOnly: false, render: (c) => (c.issues.length ? <span className="small warn">{c.issues.map(issueText).join(' ')}</span> : <span className="muted">—</span>) },
    { key: 'status', label: 'Állapot', help: 'status', ariaLabel: 'állapot', sortValue: (c) => statusOf(c, selected.has(c.external_id)).text, render: (c) => { const s = statusOf(c, selected.has(c.external_id)); return <span className={s.cls}>{s.text}</span>; } },
  ];

  const splitCount = selectedItems.filter((c) => (allocs[c.external_id] ?? []).length > 0).length;

  return (
    <main className="page">
      <div className="titles stack" style={{ gap: 4 }}>
        <h1>Binance-események megjelölése</h1>
        <div className="muted">Válassz időablakot és párt, pipáld be a kézi eseményeket. Csak a bepipált esemény kerül a naplóba (D1, D5).</div>
      </div>

      <section className="card" style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-end', gap: '16px 24px' }} aria-label="Lekérdezés">
        <div className="field">
          <HelpLabel helpKey="window" label="időablak" className="flabel">Időablak (UTC)</HelpLabel>
          <div className="seg big" role="group" aria-label="Időablak">
            {PRESETS.map((p) => (
              <button key={p.value} type="button" className={preset === p.value ? 'on' : ''} aria-pressed={preset === p.value} onClick={() => setPreset(p.value)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {preset === 'custom' ? (
          <>
            <div className="field">
              <label htmlFor="from">Kezdet (UTC)</label>
              <input id="from" type="text" inputMode="numeric" placeholder="ÉÉÉÉ-HH-NN ÓÓ:PP" className="input mono" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="to">Vég (UTC)</label>
              <input id="to" type="text" inputMode="numeric" placeholder="ÉÉÉÉ-HH-NN ÓÓ:PP" className="input mono" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
            </div>
          </>
        ) : null}
        <div className="field">
          <HelpLabel helpKey="account" label="számla" className="flabel">Számla</HelpLabel>
          <select id="acct" aria-label="Számla" className="input" style={{ minWidth: 200 }} value={account} onChange={(e) => setAccount(e.target.value as 'cross' | 'isolated')}>
            <option value="cross">Binance · Cross Margin</option>
            <option value="isolated">Binance · Isolated…</option>
          </select>
        </div>
        {account === 'isolated' ? (
          <div className="field">
            <label htmlFor="iso">Isolated pár</label>
            <input id="iso" className="input mono" value={isolatedSymbol} onChange={(e) => setIsolatedSymbol(e.target.value)} />
          </div>
        ) : null}
        <div className="field" style={{ flex: '1 1 220px' }}>
          <HelpLabel helpKey="symbols" label="párok és coinok" className="flabel">Párok (kötések)</HelpLabel>
          <input id="symbols" aria-label="Párok" className="input mono" value={symbols} onChange={(e) => setSymbols(e.target.value)} placeholder="BTCUSDC,BTCUSDT" />
        </div>
        <div className="field" style={{ flex: '1 1 220px' }}>
          <label htmlFor="assets">Coinok (kölcsön, transzfer)</label>
          <input id="assets" className="input mono" value={assets} onChange={(e) => setAssets(e.target.value)} placeholder="BTC,USDC,USDT" />
        </div>
        <button type="button" className="btn primary" disabled={!canFetch} onClick={() => void fetchList()}>
          {fetching ? 'Lekérés…' : 'Lekérés a Binance-ből'}
        </button>
        <div className="small muted" style={{ flexBasis: '100%' }}>
          Kötéslista páronként, kölcsön- és transzfer-előzmény coinonként (hipotézis, a Binance-nél mérendő).
          {list ? (
            <>
              {' '}Utolsó lekérés: <span className="mono">{formatUtc(list.fetched_at)} UTC</span> · {list.items.length} esemény.
            </>
          ) : null}
          {preset === 'custom' && !windowRange ? ' Az egyéni ablak kezdete a vége előtt kell legyen.' : ''}
        </div>
      </section>

      {saved ? (
        <Notice kind="ok" title={`${saved.created} esemény rögzítve`}>
          Ügylet: <Link to={`/deals/${saved.dealId}`}>#{saved.dealId} {saved.dealName}</Link>
          {saved.merges ? <> · {saved.merges} összefésülési javaslat vár a <Link to="/entries/new">Kézi bevitel</Link> oldalon.</> : null}
        </Notice>
      ) : null}
      {saved && saved.skipped.length ? (
        <Notice kind="warn" title={`${saved.skipped.length} esemény kimaradt`}>
          {saved.skipped.map((k) => `${k.external_id}: ${reasonText(k.reason)}`).join(' · ')}
        </Notice>
      ) : null}

      <div className="cols">
        <section className="card grow tight" aria-label="Események" style={{ padding: '16px 16px 6px' }}>
          <div className="card-head" style={{ padding: '0 4px' }}>
            <h2>Események{list ? ` · ${formatUtc(list.from).slice(0, 10)} – ${formatUtc(list.to).slice(0, 10)}` : ''}</h2>
            <div className="row-wrap small muted" style={{ alignItems: 'center' }}>
              <span className="pill bot">gbx · Margin bot</span>
              <span>szürkén, de megjelölhető</span>
            </div>
          </div>
          {list && list.notes.length ? (
            <Notice kind="warn" title="A Binance-lekérés részben hiányos">
              {list.notes.join(' · ')}
            </Notice>
          ) : null}
          {fetching ? <Loading label="Lekérés a Binance-ből…" /> : null}
          {fetchError ? <ErrorNotice error={fetchError} onRetry={() => void fetchList()} /> : null}
          {!fetching && !fetchError && !list ? <div className="muted tbl-empty">Még nincs lekérés. Válassz időablakot, majd „Lekérés a Binance-ből”.</div> : null}
          {list && !fetchError ? (
            <Table
              columns={columns}
              rows={items}
              rowKey={(c) => c.external_id}
              defaultSort={{ key: 'time', dir: 'asc' }}
              label="Binance-események"
              empty="Ebben az ablakban a Binance nem adott vissza eseményt."
              rowClass={(c) => (c.already_logged ? 'locked' : selected.has(c.external_id) ? 'sel' : c.is_bot === true ? 'bot' : undefined)}
            />
          ) : null}
          <div className="small muted" style={{ padding: '8px 4px 12px' }}>
            A kölcsön-előzmény nem ad kliens-azonosítót, ezért egy hitelfelvételről csak te tudod eldönteni, hogy kézi vagy a Margin boté. A Binance kamatrekord coinonként összesít; a kézi hitel kamata számolt (D17), itt nem jelölendő.
          </div>
        </section>

        <aside className="card side" aria-label="Ügyletbe sorolás" style={{ padding: 20, gap: 16 }}>
          <div className="stack" style={{ gap: 2 }}>
            <h2>Ügyletbe sorolás</h2>
            <div className="muted small">
              <span className="mono" style={{ color: 'var(--text)', fontWeight: 600 }} data-testid="sel-count">{selCount}</span> esemény kijelölve
            </div>
          </div>
          <div className="stack" style={{ gap: 8 }}>
            <label className="radio">
              <input type="radio" name="deal" checked={dealMode === 'new'} onChange={() => setDealMode('new')} />
              Új ügylet
            </label>
            <label className="radio">
              <input type="radio" name="deal" checked={dealMode === 'existing'} onChange={() => setDealMode('existing')} />
              Meglévő ügylet
            </label>
          </div>
          {dealMode === 'existing' ? (
            <div className="field">
              <label htmlFor="existing">Ügylet</label>
              <select id="existing" className="input" value={existingDeal ?? ''} onChange={(e) => setExistingDeal(e.target.value ? Number(e.target.value) : null)}>
                <option value="">Válassz ügyletet…</option>
                {openDeals.map((d) => (
                  <option key={d.id} value={d.id}>
                    #{d.id} {d.name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div className="field">
                <label htmlFor="dname">Ügylet neve</label>
                <input id="dname" className="input" value={dealName} onChange={(e) => setDealName(e.target.value)} placeholder="pl. BTC-short okt" />
              </div>
              <div className="field">
                <label htmlFor="dref">Referencia-coin (stablecoin-hitelnél az árszinthez, D10)</label>
                <select id="dref" className="input" value={dealRef} onChange={(e) => setDealRef(e.target.value)}>
                  <option value="BTC">BTC</option>
                  <option value="ETH">ETH</option>
                  <option value="SOL">SOL</option>
                  <option value="">Nincs</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="dtags">Címkék</label>
                <input id="dtags" className="input" value={dealTags} onChange={(e) => setDealTags(e.target.value)} placeholder="vesszővel elválasztva" />
              </div>
            </>
          )}
          <div className="box">
            <div className="card-head">
              <HelpLabel helpKey="split" label="felosztás" className="flabel">Felosztás több ügylet közt (D12)</HelpLabel>
              <button type="button" className="btn small" disabled={selCount === 0} onClick={() => setSplitting(true)}>
                Felosztás…
              </button>
            </div>
            <div className="small muted">Egy esemény mennyisége részenként több ügylethez rendelhető; az allokációk összege nem lépheti túl az esemény mennyiségét.{splitCount ? ` Felosztott események: ${splitCount}.` : ''}</div>
          </div>
          <div className="field">
            <label htmlFor="dnote">Megjegyzés</label>
            <textarea id="dnote" className="input" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {saveError ? <ErrorNotice error={saveError} /> : null}
          <button type="button" className="btn primary" style={{ width: '100%' }} disabled={!canSave} onClick={() => void save()}>
            {selCount} esemény rögzítése a naplóba
          </button>
          <div className="hint">A rögzített tőzsdei esemény változtathatatlan; címke, megjegyzés és ügylet később módosítható, minden változás naplózva (D18).</div>
        </aside>
      </div>

      {splitting ? (
        <SplitDialog
          items={selectedItems}
          deals={openDeals.map((d) => ({ id: d.id, name: d.name }))}
          initial={allocs}
          onCancel={() => setSplitting(false)}
          onSave={(next) => {
            setAllocs(next);
            setSplitting(false);
          }}
        />
      ) : null}
    </main>
  );
}

function SplitDialog({
  items,
  deals,
  initial,
  onCancel,
  onSave,
}: {
  items: Candidate[];
  deals: { id: number; name: string }[];
  initial: Record<string, AllocRow[]>;
  onCancel: () => void;
  onSave: (next: Record<string, AllocRow[]>) => void;
}) {
  const [draft, setDraft] = useState<Record<string, AllocRow[]>>(initial);
  const [current, setCurrent] = useState(items[0]?.external_id ?? '');
  const c = items.find((x) => x.external_id === current);
  const rows = draft[current] ?? [];
  const allValid = items.every((i) => {
    const r = draft[i.external_id];
    return !r || r.length === 0 || validateAllocations(r, i.quantity).ok;
  });
  return (
    <Dialog title="Felosztás több ügylet közt" onClose={onCancel}>
      <div className="small muted">Az itt megadott allokációk felülírják az alapértelmezett (egész esemény → kiválasztott ügylet) hozzárendelést az adott eseményre. A szerver a végleges ellenőrző.</div>
      <div className="field">
        <label htmlFor="split-event">Esemény</label>
        <select id="split-event" className="input" value={current} onChange={(e) => setCurrent(e.target.value)}>
          {items.map((i) => (
            <option key={i.external_id} value={i.external_id}>
              {formatUtc(i.occurred_at)} · {TYPE_LABEL[i.type]} · {i.symbol} · {formatDecimal(i.quantity)}
            </option>
          ))}
        </select>
      </div>
      {c ? (
        <AllocationEditor rows={rows} onChange={(r) => setDraft({ ...draft, [current]: r })} deals={deals} total={c.quantity} asset={c.asset} />
      ) : null}
      {c && rows.length === 0 ? (
        <button type="button" className="btn small" onClick={() => setDraft({ ...draft, [current]: [newAllocRow()] })}>
          Felosztás megadása ehhez az eseményhez
        </button>
      ) : null}
      <div className="row-wrap" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn" onClick={onCancel}>
          Mégse
        </button>
        <button type="button" className="btn primary" disabled={!allValid} onClick={() => onSave(draft)}>
          Mentés
        </button>
      </div>
    </Dialog>
  );
}
